import { Button } from "@mui/material";
import getLogger from "js/core/logger";
import { ds } from "js/core/models/dataService";
import { getPresentation } from "js/core/models/presentation";
import { FlexSpacer, Gap10, Gap20 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import { NoMatchNotice, Notice } from "js/react/components/Notice";
import Spinner from "js/react/components/Spinner";
import { BlueButton, UIPane } from "js/react/components/UiComponents";
import { renderReactDialog } from "js/react/renderReactRoot";
import { themeColors } from "js/react/sharedStyles";
import { userSlidesDataService } from "js/react/views/AddSlide/DataServices";
import {
    MultiSelectThumbnailGrid,
    Thumbnail,
    ThumbnailContainer
} from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid";
import { withTranslation } from "react-i18next";
import React, { Component } from "reactn";
import styled from "styled-components";
import { FilterDropDown } from "./Components/FilterDropDown";
import {
    InnerWhiteFrameContainer,
    SearchBarContainer,
    SearchBarInnerContainer,
    SlideSearchInput,
    UIPaneResultsContainer
} from "./Components/SearchBox";
import { CopySlideNoticeMessage } from "./CopySlideNoticeMessage";
import { PresentationLibraryDialog } from "./PresentationLibraryDialog";

const logger = getLogger();

const AddSlideButton = styled(Button)`
  background: #2EB4E5 !important;
  color: white !important;
  min-width: 150px !important;
  margin-bottom: 15px !important;
  padding: 10px 20px !important;

  &:disabled {
    background: white !important;
    color: #A9A9A9 !important;
  }
  
`;

const AddSlideContainer = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  width: 100%;
  background: ${themeColors.lightGray};
  padding-top: 20px;
  //this is to cover any thumbnail spinners behind the container
  z-index: 1000;
`;

const SearchPreviewTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 24px;
  color: #111;
`;

const filterOptionIds = ["all_content", "presentation:name", "slide:text", "slide:templateTitle"];

const getFilterOptions = t => [
    {
        id: "all_content",
        name: t("All Content")
    },
    {
        id: "presentation:name",
        name: t("Presentation Title")
    },
    {
        id: "slide:text",
        name: t("Slide Content")
    },
    {
        id: "slide:templateTitle",
        name: t("Template")
    },
];

export const CopySlidePane = withTranslation()(class CopySlidePane extends Component {
    state = {
        selectedPresentation: null,
        query: this.global.searchSlidesQuery ?? "",
        selectedContent: "all_content",
        sort: null,
        isLoaded: true,
        mainSearch: [],
        secondarySearch: [],
        searchResults: []
    }

    componentDidMount() {
        const { workspaceId } = this.props;
        const { query } = this.state;

        userSlidesDataService.ensureIndexPrepared(workspaceId);

        this.handleSearch(query);
    }

    handlePresentationSelected = async presentationId => {
        const { query } = this.state;

        const selectedPresentation = await getPresentation(presentationId, { permission: "read", autoSync: false });

        this.setState({ selectedPresentation }, () => this.handleSearch(query));
    }

    handleBrowsePresentations = () => {
        this.handleClearSelectedPresentation();
        renderReactDialog(PresentationLibraryDialog, {
            onSelectedPresentation: this.handlePresentationSelected,
            maxWidth: true
        });
    }

    fetchSearchResults = async query => {
        const { workspaceId } = this.props;
        const { selectedPresentation, selectedContent } = this.state;

        let fullTextSearchByFields;
        if (selectedContent === "all_content") {
            // Include all other options
            fullTextSearchByFields = filterOptionIds
                .filter(id => id !== "all_content");
        } else {
            fullTextSearchByFields = [selectedContent];
        }

        const searchResults = await userSlidesDataService.search({
            fullTextSearchQuery: query,
            presentationId: selectedPresentation?.id,
            workspaceId,
            fullTextSearchByFields,
        });
        this.setState({
            searchResults,
            isLoaded: true
        });
    }

    handleSearch = query => {
        const { selectedPresentation } = this.state;

        this.setState({ query });
        if (query.length > 0 || selectedPresentation) {
            this.setState({ isLoaded: false });
            this.fetchSearchResults(query);
        }
    }

    handleSelectedSlides = (selectedSlides, key) => {
        this.setState({ [key]: selectedSlides });
    }

    handleAddSlides = () => {
        const { mainSearch, secondarySearch } = this.state;
        this.props.onSelected([...mainSearch, ...secondarySearch]);
    }

    handleFilterChange = async event => {
        const { query } = this.state;
        const selectedContent = event.target.value;
        this.setState(
            {
                selectedContent,
                isLoaded: false,
            },
            () => {
                this.fetchSearchResults(query);
            });
    }

    handleClearSelectedPresentation = () => {
        this.setState({ selectedPresentation: null, mainSearch: [], secondarySearch: [] });
    }

    renderSlides = () => {
        const { forTeamSlide, t } = this.props;
        const {
            selectedPresentation,
            query,
            searchResults,
            sort,
            isLoaded,
        } = this.state;

        const allowMultiSelect = !forTeamSlide;

        if (!isLoaded) {
            return <Spinner />;
        } else if (selectedPresentation) {
            return (
                <CopySlideFromPresentationPane
                    presentation={selectedPresentation}
                    onSelected={this.props.onSelected}
                    onAddSlides={this.handleAddSlides}
                    onSelectedSlides={results => this.handleSelectedSlides(results, "mainSearch")}
                    ds={ds}
                    query={query}
                    sort={sort}
                    searchResults={searchResults}
                    forTeamSlide={forTeamSlide}
                />
            );
        } else if (query.length && searchResults.length) {
            return (
                <InnerWhiteFrameContainer>
                    <SearchPreviewTitle>{t("Results")}</SearchPreviewTitle>
                    <MultiSelectThumbnailGrid
                        columns={4}
                        colGap={30}
                        items={searchResults}
                        allowDragging={false}
                        allowMultiSelect={allowMultiSelect}
                        thumbnailClass={CopySlideThumbnail}
                        onItemSelected={results => this.handleSelectedSlides(results, "secondarySearch")}
                        onDoubleClick={results => this.handleSelectedSlides([results], "secondarySearch")}
                    />
                </InnerWhiteFrameContainer>
            );
        } else if (query.length && !searchResults.length) {
            return (
                <NoMatchNotice />
            );
        } else {
            return (
                <Notice
                    title={t("Copy slides from your existing presentations.")}
                    message={
                        <div>
                            <CopySlideNoticeMessage
                                t={t}
                                onBrowse={this.handleBrowsePresentations}
                            />
                        </div>
                    }
                />
            );
        }
    }

    render() {
        const { selectedPresentation, query, selectedContent, mainSearch, secondarySearch } = this.state;
        const { t } = this.props;
        const chip = selectedPresentation ? selectedPresentation.get("name").toUpperCase() : null;
        const isAddSlideButtonDisabled = (mainSearch.length < 1 && secondarySearch.length < 1) || (!query.length && !selectedPresentation);

        return (
            <UIPane>
                <SearchBarContainer>
                    <BlueButton onClick={this.handleBrowsePresentations} style={{ height: "100%" }}>
                        {t("Browse Presentations...")}
                    </BlueButton>
                    <Gap20 />
                    <SearchBarInnerContainer>
                        <SlideSearchInput
                            query={this.global.searchSlidesQuery || query}
                            onSearch={this.handleSearch}
                            placeholder={selectedPresentation
                                ? t("Search...")
                                : t("Search my presentations for slides...")
                            }
                            chip={chip}
                            onRemoveChip={this.handleClearSelectedPresentation}
                        />
                        <FilterDropDown
                            onChange={this.handleFilterChange}
                            selectedFilter={selectedContent}
                            filterOptions={getFilterOptions(t)}
                        />
                    </SearchBarInnerContainer>
                </SearchBarContainer>

                <UIPaneResultsContainer>
                    {this.renderSlides()}
                </UIPaneResultsContainer>

                <AddSlideContainer>
                    <AddSlideButton
                        disabled={isAddSlideButtonDisabled}
                        onClick={this.handleAddSlides}
                    >
                        {t("Copy Selected Slides")}
                    </AddSlideButton>
                </AddSlideContainer>
            </UIPane>
        );
    }
});

class CopySlideFromPresentationPane extends Component {
    render() {
        let {
            onAddSlides,
            onSelectedSlides,
            query,
            searchResults,
            forTeamSlide,
        } = this.props;

        const allowMultiSelect = !forTeamSlide;

        if (query.length && !searchResults.length) {
            return (
                <NoMatchNotice />
            );
        } else {
            return (
                <InnerWhiteFrameContainer>
                    <MultiSelectThumbnailGrid
                        columns={4}
                        colGap={30}
                        items={searchResults}
                        allowDragging={false}
                        allowMultiSelect={allowMultiSelect}
                        thumbnailClass={CopySlideThumbnail}
                        onItemSelected={onSelectedSlides}
                        onDoubleClick={onAddSlides}
                        showThemeThumbnails={true}
                    />
                </InnerWhiteFrameContainer>
            );
        }
    }
}

export const CopySlideThumbnail = withTranslation()(class CopySlideThumbnail extends Component {
    constructor() {
        super();

        this.state = {
            url: "",
            isVisible: false
        };

        this.ref = React.createRef();
    }

    componentDidMount() {
        this.observer = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting === true) {
                this.observer.disconnect();
                this.setState({ isVisible: true });
            }
        });

        this.observer.observe(this.ref.current);
    }

    componentWillUnmount() {
        this.observer.disconnect();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        const { item: { getThumbnailUrl }, showThemeThumbnails, id } = this.props;
        const { isVisible } = this.state;

        if (prevState.isVisible !== isVisible && isVisible) {
            getThumbnailUrl(showThemeThumbnails)
                .then(url => this.setState({ url }))
                .catch(err => logger.error(err, "[CopySlidePane] this.loadThumbnail() failed", { id }));
        }
    }

    render() {
        let { item, onMouseDown, onDoubleClick, selected, onClick, t } = this.props;
        let { url } = this.state;

        const index = item.index + 1;
        const slideIndexText = isNaN(index) ? "" : t("Slide {{number}}", { number: index });

        return (
            <ThumbnailContainer data-item-id={item.id} ref={this.ref}>
                <Thumbnail
                    onClick={onClick}
                    onMouseDown={onMouseDown}
                    onDoubleClick={onDoubleClick}
                    url={url}
                    selected={selected}
                    showSpinner={true}
                    style={{ border: "solid 1px #ccc", boxShadow: "0px 0px 20px rgba(0,0,0,0.05)" }} />
                <Gap10 />
                <FlexBox fillWidth>
                    <div>{item.presentationName}</div>
                    <FlexSpacer />
                    <div>{slideIndexText}</div>
                </FlexBox>
            </ThumbnailContainer>
        );
    }
});
